/**
 * Disponibilidade por boia, derivada da série de temperatura.
 *
 * **Porquê a temperatura:** todo o lote leva amostras de temperatura a 1 Hz, e a
 * série já vem com `count` por balde. Balde com `count = 0` é lote que não
 * chegou. Não é preciso pedir mais nada à API.
 *
 * **O que isto mede é a chegada de lotes à API, não a saúde do nó.** Uma boia
 * viva cujo 4G falhou conta como ausente — e é exatamente isso que se quer ver
 * numa dashboard de supervisão. O §7 do contexto do projeto é explícito: em
 * operação, a ausência de dados é a informação mais importante que há.
 */

import { densificar } from "./series";
import type { Series } from "../types";

/** Meta interna do piloto: ≥70 % de uptime por nó. */
export const META = 70;

export interface Buraco {
  inicio: number;
  fim: number;
}

export interface Disponibilidade {
  /** 0–100. `null` quando o intervalo não tem baldes nenhuns. */
  percentagem: number | null;
  /** Um por balde, na ordem do tempo: houve dados ou não. */
  baldes: boolean[];
  buracos: Buraco[];
  /** Duração do maior buraco, em ms. */
  maiorBuraco: number;
}

export function disponibilidade(serie: Series | null): Disponibilidade {
  const vazia: Disponibilidade = {
    percentagem: null,
    baldes: [],
    buracos: [],
    maiorBuraco: 0,
  };
  if (serie === null) return vazia;

  const pontos = densificar(serie);
  if (pontos.length === 0) return vazia;

  const baldes = pontos.map((p) => p.count > 0);
  const buracos: Buraco[] = [];
  let aberto: number | null = null;

  for (let i = 0; i < pontos.length; i++) {
    const ponto = pontos[i]!;
    if (!baldes[i]) {
      if (aberto === null) aberto = ponto.t;
    } else if (aberto !== null) {
      buracos.push({ inicio: aberto, fim: ponto.t });
      aberto = null;
    }
  }
  // Buraco que chega ao fim do intervalo fecha no último balde: sem isto, uma
  // boia que se calou e nunca mais falou não aparecia com buraco nenhum.
  if (aberto !== null) buracos.push({ inicio: aberto, fim: pontos[pontos.length - 1]!.t });

  const comDados = baldes.reduce((n, b) => (b ? n + 1 : n), 0);

  return {
    percentagem: (comDados / baldes.length) * 100,
    baldes,
    buracos,
    maiorBuraco: buracos.reduce((maior, b) => Math.max(maior, b.fim - b.inicio), 0),
  };
}

export type Segmento = "cheio" | "parcial" | "vazio";

/** Quantos segmentos tem a barra, independentemente do intervalo. */
export const SEGMENTOS = 120;

/**
 * Reduz os baldes a uma barra de largura fixa.
 *
 * Um intervalo de 24 h dá 1440 baldes de 1 min; desenhar um elemento por balde
 * e por boia seriam ~8600 nós no DOM, redesenhados a cada atualização. Cento e
 * vinte segmentos chegam para a barra ter ~2 px de resolução no painel.
 *
 * **Três estados, não dois.** Colapsar "parte deste segmento faltou" para
 * "faltou" exagerava os cortes; para "não faltou" escondia-os. O estado
 * intermédio não mente em nenhuma das direções, e a percentagem ao lado
 * continua calculada sobre os baldes todos.
 */
export function segmentos(baldes: boolean[], n: number = SEGMENTOS): Segmento[] {
  if (baldes.length === 0) return [];
  if (baldes.length <= n) return baldes.map((b) => (b ? "cheio" : "vazio"));

  const saida: Segmento[] = [];
  for (let i = 0; i < n; i++) {
    const de = Math.floor((i * baldes.length) / n);
    const ate = Math.max(de + 1, Math.floor(((i + 1) * baldes.length) / n));
    const fatia = baldes.slice(de, ate);
    const comDados = fatia.reduce((k, b) => (b ? k + 1 : k), 0);
    saida.push(comDados === fatia.length ? "cheio" : comDados === 0 ? "vazio" : "parcial");
  }
  return saida;
}

/** "3 buracos, o maior de 12 min". Frase única, para caber ao lado da barra. */
export function resumoBuracos(d: Disponibilidade): string {
  if (d.percentagem === null) return "sem dados no intervalo";
  if (d.buracos.length === 0) return "sem interrupções";
  const minutos = Math.round(d.maiorBuraco / 60_000);
  const duracao = minutos >= 60 ? `${(minutos / 60).toFixed(1)} h` : `${minutos} min`;
  const n = d.buracos.length;
  return `${n} ${n === 1 ? "interrupção" : "interrupções"}, a maior de ${duracao}`;
}
